import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import {
    EditorState,
    ContentState,
    ContentBlock,
    convertToRaw,
    AtomicBlockUtils,
    SelectionState,
    CharacterMetadata,
    genKey,
} from 'draft-js';
import { List, Repeat } from 'immutable';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Box, Typography, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Tooltip, MenuItem, Select } from '@mui/material';
import { AttachFile as AttachFileIcon, Close as CloseIcon, TableChart as TableChartIcon, Add as AddIcon, DeleteOutline as DeleteIcon, Code as CodeIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import MonacoEditor from '@monaco-editor/react';
import { compressImageFiles, compressImageFile } from '../utils/imageCompression';

/* ------------------------------------------------------------------ */
/*  Editable Table Block — renders inside Draft.js atomic block       */
/*  Uses LOCAL state + readOnly on Editor to prevent Draft.js from    */
/*  capturing keystrokes. Syncs to entity on blur.                    */
/* ------------------------------------------------------------------ */
const EditableTableBlock = ({ block, contentState, blockProps }) => {
    const entityKey = block.getEntityAt(0);
    const entity = entityKey ? contentState.getEntity(entityKey) : null;
    const data = entity ? entity.getData() : {};

    /* --- hooks MUST be called before any conditional return --- */
    const [localHeaders, setLocalHeaders] = useState(() => [...(data.headers || [])]);
    const [localCells, setLocalCells] = useState(() => (data.cells || []).map(r => [...r]));
    const wrapperRef = useRef(null);
    const headersRef = useRef(localHeaders);
    const cellsRef = useRef(localCells);

    if (!entityKey) return null;

    /* Backward-compat: old tables stored as tableHtml string */
    if (data.tableHtml && !data.headers) {
        return (
            <div
                style={{ margin: '8px 0', overflowX: 'auto' }}
                dangerouslySetInnerHTML={{ __html: data.tableHtml }}
            />
        );
    }

    const syncToEntity = (h, c) => {
        if (blockProps?.onEntityUpdate) {
            blockProps.onEntityUpdate(entityKey, { headers: h, cells: c });
        }
    };

    /* --- Isolation: prevent Draft.js from intercepting mouse/key events --- */
    const stopDraftJs = (e) => {
        e.stopPropagation();
    };

    /* --- Focus/Blur on the wrapper — sets editor readOnly --- */
    const handleWrapperFocus = (e) => {
        e.stopPropagation();
        if (blockProps?.setBlockEditing) blockProps.setBlockEditing(true);
    };

    const handleWrapperBlur = (e) => {
        /* If focus is moving to another element WITHIN this wrapper, do nothing */
        if (wrapperRef.current?.contains(e.relatedTarget)) return;
        if (blockProps?.setBlockEditing) blockProps.setBlockEditing(false);
        syncToEntity(headersRef.current, cellsRef.current);
    };

    /* --- Text editing: update LOCAL state only (no entity sync) --- */
    const updateHeader = (ci, val) => {
        setLocalHeaders(prev => {
            const next = prev.map((v, i) => i === ci ? val : v);
            headersRef.current = next;
            return next;
        });
    };
    const updateCell = (ri, ci, val) => {
        setLocalCells(prev => {
            const next = prev.map((row, r) =>
                row.map((v, c2) => (r === ri && c2 === ci) ? val : v)
            );
            cellsRef.current = next;
            return next;
        });
    };

    /* --- Structural operations: update local state + sync immediately --- */
    const addRow = (e) => {
        e.stopPropagation();
        const newCells = [...cellsRef.current, Array(headersRef.current.length).fill('')];
        setLocalCells(newCells);
        cellsRef.current = newCells;
        syncToEntity(headersRef.current, newCells);
    };
    const deleteRow = (ri, e) => {
        if (e) e.stopPropagation();
        if (cellsRef.current.length <= 1) return;
        const newCells = cellsRef.current.filter((_, i) => i !== ri);
        setLocalCells(newCells);
        cellsRef.current = newCells;
        syncToEntity(headersRef.current, newCells);
    };
    const addColumn = (e) => {
        if (e) e.stopPropagation();
        const newHeaders = [...headersRef.current, `Header ${headersRef.current.length + 1}`];
        const newCells = cellsRef.current.map(row => [...row, '']);
        setLocalHeaders(newHeaders);
        setLocalCells(newCells);
        headersRef.current = newHeaders;
        cellsRef.current = newCells;
        syncToEntity(newHeaders, newCells);
    };
    const deleteColumn = (ci, e) => {
        if (e) e.stopPropagation();
        if (headersRef.current.length <= 1) return;
        const newHeaders = headersRef.current.filter((_, i) => i !== ci);
        const newCells = cellsRef.current.map(row => row.filter((_, i) => i !== ci));
        setLocalHeaders(newHeaders);
        setLocalCells(newCells);
        headersRef.current = newHeaders;
        cellsRef.current = newCells;
        syncToEntity(newHeaders, newCells);
    };

    const cellStyle = {
        width: '100%', border: 'none', padding: '6px 10px',
        background: 'transparent', fontSize: '13px', outline: 'none',
        boxSizing: 'border-box',
    };

    return (
        <div ref={wrapperRef}
             onMouseDown={stopDraftJs}
             onClick={stopDraftJs}
             onKeyDown={stopDraftJs}
             onFocus={handleWrapperFocus} onBlur={handleWrapperBlur}
             style={{ margin: '8px 0', overflowX: 'auto', position: 'relative', zIndex: 1 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
                <thead>
                    <tr>
                        {localHeaders.map((h, ci) => (
                            <th key={ci} style={{ border: '1px solid #ccc', padding: 0, background: '#f0f4f8', position: 'relative', minWidth: 100 }}>
                                <input
                                    type="text" value={h}
                                    onChange={(e) => updateHeader(ci, e.target.value)}
                                    onMouseDown={stopDraftJs}
                                    onClick={stopDraftJs}
                                    onKeyDown={stopDraftJs}
                                    style={{ ...cellStyle, fontWeight: 600 }}
                                    placeholder={`Header ${ci + 1}`}
                                />
                                {localHeaders.length > 1 && (
                                    <IconButton size="small" onClick={(e) => deleteColumn(ci, e)}
                                        onMouseDown={stopDraftJs}
                                        sx={{ position: 'absolute', top: -2, right: -2, p: '1px', opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                        <CloseIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                )}
                            </th>
                        ))}
                        <th style={{ border: 'none', padding: 0, width: 28 }}>
                            <Tooltip title="Add column" arrow>
                                <IconButton size="small" onClick={addColumn} onMouseDown={stopDraftJs} sx={{ p: '2px' }}>
                                    <AddIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                                </IconButton>
                            </Tooltip>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {localCells.map((row, ri) => (
                        <tr key={ri}>
                            {row.map((cell, ci) => (
                                <td key={ci} style={{ border: '1px solid #ccc', padding: 0, verticalAlign: 'top' }}>
                                    <input
                                        type="text" value={cell}
                                        onChange={(e) => updateCell(ri, ci, e.target.value)}
                                        onMouseDown={stopDraftJs}
                                        onClick={stopDraftJs}
                                        onKeyDown={stopDraftJs}
                                        style={cellStyle}
                                        placeholder="..."
                                    />
                                </td>
                            ))}
                            <td style={{ border: 'none', padding: 0, width: 28, verticalAlign: 'middle' }}>
                                {localCells.length > 1 && (
                                    <Tooltip title="Delete row" arrow>
                                        <IconButton size="small" onClick={(e) => deleteRow(ri, e)} onMouseDown={stopDraftJs} sx={{ p: '2px', opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                            <DeleteIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<AddIcon />} onClick={addRow} onMouseDown={stopDraftJs}
                    sx={{ textTransform: 'none', fontSize: 11, py: 0, minHeight: 24, color: '#1976d2' }}>
                    Add Row
                </Button>
            </Box>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Image / Video block renderer (unchanged)                          */
/* ------------------------------------------------------------------ */
const ImageVideoBlock = (props) => {
    const entityKey = props.block.getEntityAt(0);
    if (!entityKey) return null;

    const entity = props.contentState.getEntity(entityKey);
    const type = entity.getType();
    const data = entity.getData();
    const src = data.src;
    const displayName = data.fileName || data.alt || '';

    if (type === 'IMAGE') {
        return (
            <div style={{ margin: '8px 0', textAlign: 'center' }}>
                <img
                    src={src}
                    alt={displayName || 'Image'}
                    style={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        borderRadius: 6,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                />
                {displayName && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{displayName}</div>
                )}
            </div>
        );
    }

    if (type === 'VIDEO') {
        // Append #t=0.001 to force the browser to decode and display
        // the first video frame instead of showing a black rectangle.
        const videoSrc = src && !src.includes('#t=') ? `${src}#t=0.001` : src;
        return (
            <div style={{ margin: '8px 0', textAlign: 'center' }}>
                <video
                    src={videoSrc}
                    controls
                    preload="metadata"
                    style={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        borderRadius: 6,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                />
                {displayName && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{displayName}</div>
                )}
            </div>
        );
    }

    return null;
};

/* ------------------------------------------------------------------ */
/*  Editable Code Block — renders a styled code editor area           */
/*  Uses LOCAL state + readOnly on Editor to prevent keystroke leaks. */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/*  Language options for the code block selector                       */
/* ------------------------------------------------------------------ */
const CODE_LANGUAGES = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'csharp', label: 'C#' },
    { value: 'cpp', label: 'C++' },
    { value: 'go', label: 'Go' },
    { value: 'rust', label: 'Rust' },
    { value: 'sql', label: 'SQL' },
    { value: 'html', label: 'HTML' },
    { value: 'css', label: 'CSS' },
    { value: 'json', label: 'JSON' },
    { value: 'xml', label: 'XML' },
    { value: 'yaml', label: 'YAML' },
    { value: 'bash', label: 'Shell / Bash' },
    { value: 'plaintext', label: 'Plain Text' },
];

const EditableCodeBlock = ({ block, contentState, blockProps }) => {
    const entityKey = block.getEntityAt(0);
    const entity = entityKey ? contentState.getEntity(entityKey) : null;
    const data = entity ? entity.getData() : {};

    const [localCode, setLocalCode] = useState(() => data.code || '');
    const [language, setLanguage] = useState(() => data.language || 'javascript');
    const [copied, setCopied] = useState(false);
    const codeRef = useRef(localCode);
    const languageRef = useRef(language);
    const wrapperRef = useRef(null);
    const editorMountedRef = useRef(false);

    if (!entityKey) return null;

    /* --- Isolation: prevent Draft.js from intercepting events --- */
    const stopDraftJs = (e) => {
        e.stopPropagation();
    };

    const handleWrapperFocus = (e) => {
        e.stopPropagation();
        if (blockProps?.setBlockEditing) blockProps.setBlockEditing(true);
    };

    const handleWrapperBlur = (e) => {
        if (wrapperRef.current?.contains(e.relatedTarget)) return;
        if (blockProps?.setBlockEditing) blockProps.setBlockEditing(false);
        if (blockProps?.onEntityUpdate) {
            blockProps.onEntityUpdate(entityKey, { code: codeRef.current, language: languageRef.current });
        }
    };

    const handleCodeChange = (value) => {
        const v = value || '';
        setLocalCode(v);
        codeRef.current = v;
    };

    const handleLanguageChange = (e) => {
        e.stopPropagation();
        const lang = e.target.value;
        setLanguage(lang);
        languageRef.current = lang;
        // Sync entity immediately so language persists
        if (blockProps?.onEntityUpdate) {
            blockProps.onEntityUpdate(entityKey, { code: codeRef.current, language: lang });
        }
    };

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(codeRef.current).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleEditorMount = (editor) => {
        editorMountedRef.current = true;
        // Prevent Draft.js from capturing keystrokes inside Monaco
        editor.onKeyDown((e) => {
            e.stopPropagation();
        });
    };

    /* Calculate height based on line count (min 3 lines, max 20) */
    const lineCount = Math.max(3, Math.min(20, (localCode || '').split('\n').length + 1));
    const editorHeight = lineCount * 19 + 16; /* ~19px per line + padding */

    return (
        <div ref={wrapperRef}
             onMouseDown={stopDraftJs}
             onClick={stopDraftJs}
             onKeyDown={stopDraftJs}
             onFocus={handleWrapperFocus} onBlur={handleWrapperBlur}
             style={{ margin: '8px 0', position: 'relative', zIndex: 1 }}>
            <Box sx={{
                borderRadius: '8px', overflow: 'hidden',
                border: '1px solid #e0e0e0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}>
                {/* Header bar */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.5, py: 0.5,
                    background: '#f5f7f9', borderBottom: '1px solid #e0e0e0',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CodeIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Select
                            value={language}
                            onChange={handleLanguageChange}
                            onMouseDown={stopDraftJs}
                            size="small"
                            variant="standard"
                            disableUnderline
                            sx={{
                                fontSize: 12, color: '#555',
                                '.MuiSelect-select': { py: 0.25, px: 0.5 },
                            }}
                        >
                            {CODE_LANGUAGES.map(l => (
                                <MenuItem key={l.value} value={l.value} sx={{ fontSize: 12 }}>
                                    {l.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Tooltip title={copied ? 'Copied!' : 'Copy code'} arrow>
                        <IconButton size="small" onClick={handleCopy} onMouseDown={stopDraftJs}
                            sx={{ p: 0.5, color: copied ? '#4caf50' : '#888' }}>
                            <CopyIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                {/* Monaco Editor */}
                <Box onMouseDown={stopDraftJs} onClick={stopDraftJs}>
                    <MonacoEditor
                        height={editorHeight}
                        language={language}
                        value={localCode}
                        onChange={handleCodeChange}
                        onMount={handleEditorMount}
                        theme="vs-light"
                        options={{
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            lineNumbers: 'on',
                            lineNumbersMinChars: 3,
                            glyphMargin: false,
                            folding: false,
                            fontSize: 13,
                            fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                            renderLineHighlight: 'line',
                            scrollbar: {
                                vertical: 'auto',
                                horizontal: 'auto',
                                verticalScrollbarSize: 8,
                                horizontalScrollbarSize: 8,
                            },
                            padding: { top: 8, bottom: 8 },
                            wordWrap: 'on',
                            automaticLayout: true,
                            tabSize: 2,
                            overviewRulerLanes: 0,
                            hideCursorInOverviewRuler: true,
                            overviewRulerBorder: false,
                            contextmenu: false,
                        }}
                    />
                </Box>
            </Box>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Atomic block router — delegates to table, code, or image/video    */
/* ------------------------------------------------------------------ */
const AtomicBlockRouter = (props) => {
    const entityKey = props.block.getEntityAt(0);
    if (!entityKey) return null;
    const entity = props.contentState.getEntity(entityKey);
    const type = entity.getType();
    if (type === 'TABLE') return <EditableTableBlock {...props} />;
    if (type === 'CODE_BLOCK') return <EditableCodeBlock {...props} />;
    return <ImageVideoBlock {...props} />;
};

/* ------------------------------------------------------------------ */
/*  Custom entity transform — outputs [IMAGE: …] / [VIDEO: …] markers*/
/*  so the saved HTML stays backward-compatible with                  */
/*  IssueContentRenderer's placeholder regex.                         */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/*  Encode / decode helpers for round-trippable markers               */
/* ------------------------------------------------------------------ */
const encodeMarkerData = (obj) => {
    try {
        return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
    } catch { return ''; }
};
const decodeMarkerData = (b64) => {
    try {
        return JSON.parse(decodeURIComponent(escape(atob(b64))));
    } catch { return null; }
};

const customEntityTransform = (entity, text) => {
    if (entity.type === 'IMAGE') {
        const fileName = entity.data.fileName || entity.data.alt || 'image.png';
        return `[IMAGE: ${fileName}]`;
    }
    if (entity.type === 'VIDEO') {
        const fileName = entity.data.fileName || 'recording';
        return `[VIDEO: ${fileName}]`;
    }
    if (entity.type === 'TABLE') {
        const { headers, cells, tableHtml } = entity.data;
        /* Backward-compat: old tables stored as raw HTML */
        if (tableHtml && !headers) return tableHtml;
        const encoded = encodeMarkerData({ headers, cells });
        return `[TABLE:${encoded}]`;
    }
    if (entity.type === 'CODE_BLOCK') {
        const code = entity.data.code || '';
        const language = entity.data.language || 'plaintext';
        const encoded = encodeMarkerData({ code, language });
        return `[CODE:${encoded}]`;
    }
    // Return undefined for all other entity types → default handling
    return undefined;
};

/* ------------------------------------------------------------------ */
/*  Helper: convert raw content state → HTML using our entity transform*/
/* ------------------------------------------------------------------ */
const toHtml = (rawContentState) => draftToHtml(rawContentState, null, false, customEntityTransform);

/* ------------------------------------------------------------------ */
/*  Helper: manually insert an atomic block at the end of the editor  */
/*  content. This BYPASSES AtomicBlockUtils.insertAtomicBlock which   */
/*  requires a valid focused selection and fails silently when the    */
/*  MUI Dialog steals focus.                                         */
/* ------------------------------------------------------------------ */
const insertAtomicBlockAtEnd = (editorState, entityType, mutability, entityData) => {
    let content = editorState.getCurrentContent();

    /* 1. Create the entity */
    content = content.createEntity(entityType, mutability, entityData);
    const entityKey = content.getLastCreatedEntityKey();

    /* 2. Build atomic block with character metadata pointing to entity */
    const charData = CharacterMetadata.create({ entity: entityKey });
    const atomicKey = genKey();
    const atomicBlock = new ContentBlock({
        key: atomicKey,
        type: 'atomic',
        text: ' ',
        characterList: List(Repeat(charData, 1)),
    });

    /* 3. Build trailing empty block so cursor has somewhere to go */
    const trailKey = genKey();
    const trailBlock = new ContentBlock({
        key: trailKey,
        type: 'unstyled',
        text: '',
        characterList: List(),
    });

    /* 4. Append both blocks to the existing block map */
    let blockMap = content.getBlockMap();
    blockMap = blockMap.set(atomicKey, atomicBlock);
    blockMap = blockMap.set(trailKey, trailBlock);

    const newContent = content.merge({
        blockMap,
        selectionAfter: SelectionState.createEmpty(trailKey),
    });

    /* 5. Push into editor state and move cursor to trailing block */
    const pushed = EditorState.push(editorState, newContent, 'insert-fragment');
    return EditorState.forceSelection(
        pushed,
        SelectionState.createEmpty(trailKey).merge({
            anchorOffset: 0, focusOffset: 0, hasFocus: true,
        }),
    );
};

/* ------------------------------------------------------------------ */
/*  Helper: ensure there is always an empty unstyled block at the end */
/*  of the content so the user can place their cursor after images.   */
/* ------------------------------------------------------------------ */
const ensureTrailingEmptyBlock = (editorState) => {
    const content = editorState.getCurrentContent();
    const blockMap = content.getBlockMap();
    const lastBlock = blockMap.last();

    // If the last block is already an empty unstyled block, just move cursor there
    if (lastBlock.getType() === 'unstyled' && lastBlock.getText() === '') {
        const sel = SelectionState.createEmpty(lastBlock.getKey()).merge({
            anchorOffset: 0, focusOffset: 0,
        });
        return EditorState.forceSelection(editorState, sel);
    }

    // Otherwise, append a new empty unstyled block and move cursor to it
    const newBlockKey = genKey();
    const newBlock = new ContentBlock({
        key: newBlockKey,
        type: 'unstyled',
        text: '',
        characterList: List(),
    });
    const newBlockMap = blockMap.set(newBlockKey, newBlock);
    const newContent = content.merge({ blockMap: newBlockMap });
    const newState = EditorState.push(editorState, newContent, 'insert-fragment');
    const sel = SelectionState.createEmpty(newBlockKey).merge({
        anchorOffset: 0, focusOffset: 0,
    });
    return EditorState.forceSelection(newState, sel);
};

/* ------------------------------------------------------------------ */
/*  Helper: Pre-process HTML containing [IMAGE: …] placeholders into  */
/*  <img> tags so htmlToDraft can convert them into atomic blocks.     */
/*  VIDEO markers are kept as text with the blob URL encoded in,      */
/*  because htmlToDraft does NOT support <video> tags. Video blocks    */
/*  are created post-parse in buildEditorState (like TABLE / CODE).   */
/* ------------------------------------------------------------------ */
const preprocessHtmlWithMedia = (html, existingMedia) => {
    if (!html || !existingMedia || existingMedia.length === 0) return html;
    let mediaIdx = 0;
    return html.replace(/\[(IMAGE|VIDEO):\s*([^\]]+)\]/g, (match, type, fileName) => {
        if (mediaIdx < existingMedia.length) {
            const url = existingMedia[mediaIdx++];
            if (type === 'IMAGE') {
                return `<img src="${url}" alt="${fileName.trim()}" />`;
            }
            // Encode video URL into the marker so buildEditorState can create
            // a proper atomic VIDEO block after htmlToDraft parsing.
            return `[VIDEO: ${fileName.trim()}|${url}]`;
        }
        return match;
    });
};

/* ------------------------------------------------------------------ */
/*  Helper: build EditorState from HTML + optional existing media URLs */
/*  Detects [TABLE:...] and [CODE:...] markers in the parsed content  */
/*  and replaces them with proper atomic entity blocks so tables and   */
/*  code blocks survive the save → load round-trip.                   */
/* ------------------------------------------------------------------ */
const buildEditorState = (html, existingMedia) => {
    if (!html) return EditorState.createEmpty();
    // Only use URLs the browser can render directly (blob: or data:)
    const usableMedia = (existingMedia || []).filter(u =>
        u && (u.startsWith('blob:') || u.startsWith('data:'))
    );
    const processed = preprocessHtmlWithMedia(html, usableMedia);
    const contentBlock = htmlToDraft(processed);
    if (contentBlock) {
        let contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks,
            contentBlock.entityMap
        );

        /* --- Scan blocks for TABLE / CODE / VIDEO markers and replace with atomic blocks --- */
        const tableRe = /\[TABLE:([A-Za-z0-9+/=]+)\]/;
        const codeRe = /\[CODE:([A-Za-z0-9+/=]+)\]/;
        const videoRe = /\[VIDEO:\s*([^|\]]+)\|([^\]]+)\]/;
        const videoPlainRe = /\[VIDEO:\s*([^\]]+)\]/;
        let blockMap = contentState.getBlockMap();
        let changed = false;

        /* Track usable media index for VIDEO markers without encoded URLs */
        let videoMediaIdx = 0;

        blockMap.forEach((block, key) => {
            const text = block.getText();
            let markerMatch = text.match(tableRe);
            let entityType = 'TABLE';
            let mutability = 'IMMUTABLE';

            if (!markerMatch) {
                markerMatch = text.match(codeRe);
                entityType = 'CODE_BLOCK';
                mutability = 'MUTABLE';
            }

            if (markerMatch) {
                const decoded = decodeMarkerData(markerMatch[1]);
                if (decoded) {
                    contentState = contentState.createEntity(entityType, mutability, decoded);
                    const ek = contentState.getLastCreatedEntityKey();
                    const charData = CharacterMetadata.create({ entity: ek });
                    const atomicBlock = new ContentBlock({
                        key: block.getKey(),
                        type: 'atomic',
                        text: ' ',
                        characterList: List(Repeat(charData, 1)),
                    });
                    blockMap = blockMap.set(key, atomicBlock);
                    changed = true;
                }
                return; // skip to next block
            }

            /* --- VIDEO marker with encoded URL: [VIDEO: filename|blob:...] --- */
            const videoUrlMatch = text.match(videoRe);
            if (videoUrlMatch) {
                const fileName = videoUrlMatch[1].trim();
                const src = videoUrlMatch[2].trim();
                contentState = contentState.createEntity('VIDEO', 'IMMUTABLE', { src, fileName });
                const ek = contentState.getLastCreatedEntityKey();
                const charData = CharacterMetadata.create({ entity: ek });
                const atomicBlock = new ContentBlock({
                    key: block.getKey(),
                    type: 'atomic',
                    text: ' ',
                    characterList: List(Repeat(charData, 1)),
                });
                blockMap = blockMap.set(key, atomicBlock);
                changed = true;
                return;
            }

            /* --- VIDEO marker without URL (no existing media): [VIDEO: filename] --- */
            const videoPlainMatch = text.match(videoPlainRe);
            if (videoPlainMatch && usableMedia.length > 0) {
                /* Try to find a usable video URL from the remaining media */
                if (videoMediaIdx < usableMedia.length) {
                    const fileName = videoPlainMatch[1].trim();
                    const src = usableMedia[videoMediaIdx++];
                    contentState = contentState.createEntity('VIDEO', 'IMMUTABLE', { src, fileName });
                    const ek = contentState.getLastCreatedEntityKey();
                    const charData = CharacterMetadata.create({ entity: ek });
                    const atomicBlock = new ContentBlock({
                        key: block.getKey(),
                        type: 'atomic',
                        text: ' ',
                        characterList: List(Repeat(charData, 1)),
                    });
                    blockMap = blockMap.set(key, atomicBlock);
                    changed = true;
                }
            }
        });

        if (changed) {
            contentState = contentState.merge({ blockMap });
        }

        let es = EditorState.createWithContent(contentState);
        // Guarantee a trailing empty block so the user can type below images
        const lastBlock = contentState.getBlockMap().last();
        if (lastBlock && (lastBlock.getType() === 'atomic' || lastBlock.getText().match(/\[(IMAGE|VIDEO):/))) {
            es = ensureTrailingEmptyBlock(es);
        }
        return es;
    }
    return EditorState.createEmpty();
};

/**
 * RichTextEditor Component
 *
 * A rich text editor with **inline** media rendering via drag-and-drop,
 * file selection, or the toolbar image button.
 *
 * Dropped / selected images are read as base64 and inserted as Draft.js
 * atomic blocks so they render immediately inside the editor.
 * On save, the HTML output preserves [IMAGE: …] / [VIDEO: …] markers
 * for backward compatibility with IssueContentRenderer.
 *
 * @param {Object}   props
 * @param {string}   props.value         - Initial HTML content
 * @param {Function} props.onChange       - Callback(content, mediaFiles)
 * @param {string}   props.placeholder   - Placeholder text
 * @param {Array}    props.existingMedia  - Existing media proxy URLs (for edit mode)
 */
/* ------------------------------------------------------------------ */
/*  Custom toolbar button for inserting tables                        */
/*  NOTE: Does NOT use the `editorState` prop from react-draft-wysiwyg*/
/*  because that becomes stale when the dialog steals focus.          */
/*  Instead it calls `onInsertTable(rows, cols)` which the parent     */
/*  RichTextEditor provides — that callback always reads the fresh    */
/*  editorStateRef.current.                                           */
/* ------------------------------------------------------------------ */
const TableOption = ({ onInsertTable }) => {
    const [open, setOpen] = React.useState(false);
    const [rows, setRows] = React.useState(3);
    const [cols, setCols] = React.useState(3);

    const handleInsert = () => {
        const r = rows;
        const c = cols;
        /* Close dialog FIRST so focus returns to the editor before we
           mutate the Draft.js state — prevents the MUI Dialog focus-lock
           from interfering with Draft.js internal focus handling.      */
        setOpen(false);
        setRows(3);
        setCols(3);
        /* Allow one frame for the Dialog exit transition to release focus,
           then insert the table into the now-focused editor.            */
        setTimeout(() => {
            if (onInsertTable) onInsertTable(r, c);
        }, 120);
    };

    return (
        <>
            <Tooltip title="Insert Table" arrow>
                <IconButton size="small" onClick={() => setOpen(true)} sx={{ p: 0.5 }}>
                    <TableChartIcon fontSize="small" sx={{ color: '#333' }} />
                </IconButton>
            </Tooltip>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="xs"
                fullWidth
                disableEnforceFocus
                disableAutoFocus
                disableRestoreFocus
            >
                <DialogTitle>Insert Table</DialogTitle>
                <DialogContent sx={{ display: 'flex', gap: 2, pt: 2 }}>
                    <TextField
                        label="Rows"
                        type="number"
                        value={rows}
                        onChange={(e) => setRows(parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 20 }}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label="Columns"
                        type="number"
                        value={cols}
                        onChange={(e) => setCols(parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 10 }}
                        size="small"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleInsert}>Insert</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

/* ------------------------------------------------------------------ */
/*  Custom toolbar button for inserting code blocks                   */
/* ------------------------------------------------------------------ */
const CodeSnippetOption = ({ onInsertCodeBlock }) => {
    return (
        <Tooltip title="Insert Code Block" arrow>
            <IconButton size="small" onClick={() => { if (onInsertCodeBlock) onInsertCodeBlock(); }} sx={{ p: 0.5 }}>
                <CodeIcon fontSize="small" sx={{ color: '#333' }} />
            </IconButton>
        </Tooltip>
    );
};

const RichTextEditor = ({ value = '', onChange, placeholder = 'Describe the issue...', existingMedia = [] }) => {
    /* ---- state ---- */
    const [editorState, setEditorState] = useState(() => buildEditorState(value, existingMedia));
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaPlaceholders, setMediaPlaceholders] = useState([]);
    const [blockEditing, setBlockEditing] = useState(false);

    /* ---- refs ---- */
    const fileInputRef = useRef(null);
    const previousValueRef = useRef(value);
    const isInitialMount = useRef(true);
    const isInternalUpdate = useRef(false);
    const mediaFilesRef = useRef([]);
    const editorStateRef = useRef(editorState);

    // Keep editorStateRef in sync
    useEffect(() => { editorStateRef.current = editorState; }, [editorState]);

    /* ------------------------------------------------ */
    /*  Table update callback — syncs cell edits back   */
    /*  into entity data and triggers editor onChange    */
    /* ------------------------------------------------ */
    const handleEntityUpdate = useCallback((entityKey, newData) => {
        const currentState = editorStateRef.current;
        const cs = currentState.getCurrentContent();
        cs.replaceEntityData(entityKey, newData);
        const newEditorState = EditorState.push(currentState, cs, 'apply-entity');
        setEditorState(newEditorState);
        isInternalUpdate.current = true;
        const raw = convertToRaw(newEditorState.getCurrentContent());
        if (onChange) onChange(toHtml(raw), mediaFilesRef.current);
    }, [onChange]);

    /* ------------------------------------------------ */
    /*  Block renderer — routes atomic blocks to the    */
    /*  correct component (table, code, or image/video) */
    /* ------------------------------------------------ */
    const customBlockRenderer = useCallback((block) => {
        if (block.getType() === 'atomic') {
            return {
                component: AtomicBlockRouter,
                editable: false,
                props: { onEntityUpdate: handleEntityUpdate, setBlockEditing },
            };
        }
        return null;
    }, [handleEntityUpdate]);

    /* ------------------------------------------------ */
    /*  Insert Table — uses editorStateRef (always fresh)*/
    /*  Uses insertAtomicBlockAtEnd which manually       */
    /*  appends blocks, bypassing broken selection state */
    /* ------------------------------------------------ */
    const insertTableBlock = useCallback((rows, cols) => {
        setBlockEditing(false);

        const r = Math.max(1, Math.min(rows, 20));
        const c = Math.max(1, Math.min(cols, 10));
        const headers = Array.from({ length: c }, (_, i) => `Header ${i + 1}`);
        const cellData = Array.from({ length: r - 1 }, () => Array(c).fill(''));

        const newState = insertAtomicBlockAtEnd(
            editorStateRef.current,
            'TABLE', 'IMMUTABLE', { headers, cells: cellData },
        );

        setEditorState(newState);
        isInternalUpdate.current = true;
        const raw = convertToRaw(newState.getCurrentContent());
        if (onChange) onChange(toHtml(raw), mediaFilesRef.current);
    }, [onChange]);

    /* ------------------------------------------------ */
    /*  Insert Code Block — same approach               */
    /* ------------------------------------------------ */
    const insertCodeBlock = useCallback(() => {
        setBlockEditing(false);

        const newState = insertAtomicBlockAtEnd(
            editorStateRef.current,
            'CODE_BLOCK', 'MUTABLE', { code: '', language: 'javascript' },
        );

        setEditorState(newState);
        isInternalUpdate.current = true;
        const raw = convertToRaw(newState.getCurrentContent());
        if (onChange) onChange(toHtml(raw), mediaFilesRef.current);
    }, [onChange]);

    /* ------------------------------------------------ */
    /*  Sync editor when value prop changes externally  */
    /* ------------------------------------------------ */
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            previousValueRef.current = value;
            return;
        }
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            previousValueRef.current = value;
            return;
        }
        if (value !== previousValueRef.current) {
            previousValueRef.current = value;
            setEditorState(buildEditorState(value, existingMedia));
            setMediaFiles([]);
            mediaFilesRef.current = [];
            setMediaPlaceholders([]);
        }
    }, [value, existingMedia]);

    /* ------------------------------------------------ */
    /*  Editor change handler                           */
    /* ------------------------------------------------ */
    const handleEditorChange = useCallback((newEditorState) => {
        setEditorState(newEditorState);
        isInternalUpdate.current = true;

        const raw = convertToRaw(newEditorState.getCurrentContent());
        const html = toHtml(raw);

        if (onChange) {
            onChange(html, mediaFilesRef.current);
        }
    }, [onChange]);

    /* ------------------------------------------------ */
    /*  Drag-and-drop handler                           */
    /* ------------------------------------------------ */
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer.files).filter(
            f => f.type.startsWith('image/') || f.type.startsWith('video/')
        );
        if (files.length > 0) addMediaFiles(files);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) addMediaFiles(files);
    };

    /* ------------------------------------------------ */
    /*  Core: read files as base64 → insert atomic blks */
    /* ------------------------------------------------ */
    const addMediaFiles = async (files) => {
        // Compress images client-side before preview & upload
        const processedFiles = await compressImageFiles(files);

        // Build placeholder entries for the bottom thumbnail panel
        const newPlaceholders = processedFiles.map((file, i) => ({
            id: `media_${Date.now()}_${i}`,
            name: file.name,
            type: file.type.startsWith('image/') ? 'image' : 'video',
            file,
        }));
        setMediaPlaceholders(prev => [...prev, ...newPlaceholders]);

        // Track raw File objects for the upload-on-save flow
        const updatedMediaFiles = [...mediaFilesRef.current, ...processedFiles];
        setMediaFiles(updatedMediaFiles);
        mediaFilesRef.current = updatedMediaFiles;

        // Read every file as base64, then insert atomic blocks
        const readPromises = processedFiles.map(file =>
            new Promise(resolve => {
                const reader = new FileReader();
                reader.onload = (e) => resolve({
                    src: e.target.result,
                    fileName: file.name,
                    entityType: file.type.startsWith('image/') ? 'IMAGE' : 'VIDEO',
                });
                reader.readAsDataURL(file);
            })
        );

        Promise.all(readPromises).then(mediaDataList => {
            let currentState = editorStateRef.current;

            mediaDataList.forEach(({ src, fileName, entityType }) => {
                const cs = currentState.getCurrentContent()
                    .createEntity(entityType, 'IMMUTABLE', { src, fileName });
                const entityKey = cs.getLastCreatedEntityKey();
                currentState = AtomicBlockUtils.insertAtomicBlock(currentState, entityKey, ' ');
            });

            // Ensure there's an empty block after the last image so the user can type below it
            currentState = ensureTrailingEmptyBlock(currentState);

            setEditorState(currentState);

            // Notify parent
            if (onChange) {
                isInternalUpdate.current = true;
                const raw = convertToRaw(currentState.getCurrentContent());
                onChange(toHtml(raw), updatedMediaFiles);
            }
        });
    };

    /* ------------------------------------------------ */
    /*  Remove a file from the bottom panel             */
    /* ------------------------------------------------ */
    const removeMediaFile = (index) => {
        const updatedMediaFiles = mediaFiles.filter((_, i) => i !== index);
        setMediaFiles(updatedMediaFiles);
        mediaFilesRef.current = updatedMediaFiles;
        setMediaPlaceholders(prev => prev.filter((_, i) => i !== index));

        if (onChange) {
            isInternalUpdate.current = true;
            const raw = convertToRaw(editorState.getCurrentContent());
            onChange(toHtml(raw), updatedMediaFiles);
        }
    };

    /* ------------------------------------------------ */
    /*  Toolbar image-upload callback (base64 preview)  */
    /* ------------------------------------------------ */
    const imageUploadCallback = useCallback((file) => {
        return new Promise(async (resolve) => {
            // Compress image before preview & tracking
            const compressed = await compressImageFile(file);

            // Track file for save-time upload
            const updated = [...mediaFilesRef.current, compressed];
            setMediaFiles(updated);
            mediaFilesRef.current = updated;
            setMediaPlaceholders(prev => [...prev, {
                id: `media_${Date.now()}`,
                name: compressed.name,
                type: 'image',
                file: compressed,
            }]);

            const reader = new FileReader();
            reader.onload = (e) => resolve({ data: { link: e.target.result } });
            reader.readAsDataURL(compressed);
        });
    }, []);

    /* ------------------------------------------------ */
    /*  Clipboard paste handler — supports pasting      */
    /*  screenshots directly from clipboard (Ctrl+V)    */
    /* ------------------------------------------------ */
    const handlePastedFiles = useCallback((files) => {
        const mediaFilesList = files.filter(
            f => f.type.startsWith('image/') || f.type.startsWith('video/')
        );
        if (mediaFilesList.length > 0) {
            addMediaFiles(mediaFilesList);
            return 'handled';
        }
        return 'not-handled';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    /* ------------------------------------------------ */
    /*  Click handler: clicking in the blank space      */
    /*  below media blocks ensures cursor lands in an   */
    /*  editable unstyled block at the end.             */
    /* ------------------------------------------------ */
    const handleEditorWrapperClick = useCallback((e) => {
        // Only act when the click landed on the editor wrapper area itself,
        // not on actual content (text, images, toolbar, etc.)
        const target = e.target;
        const isEditorArea =
            target.classList?.contains('DraftEditor-root') ||
            target.classList?.contains('public-DraftEditor-content') ||
            target.classList?.contains('rdw-editor-main') ||
            target.getAttribute?.('data-contents') === 'true';
        if (!isEditorArea) return;

        const state = ensureTrailingEmptyBlock(editorStateRef.current);
        setEditorState(state);
        isInternalUpdate.current = true;
        const raw = convertToRaw(state.getCurrentContent());
        if (onChange) onChange(toHtml(raw), mediaFilesRef.current);
    }, [onChange]);

    /* ================================================ */
    /*  Render                                          */
    /* ================================================ */
    return (
        <Box>
            <Paper
                variant="outlined"
                sx={{
                    minHeight: 300,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'text',
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={handleEditorWrapperClick}
            >
                <Editor
                    editorState={editorState}
                    onEditorStateChange={handleEditorChange}
                    readOnly={blockEditing}
                    placeholder={placeholder}
                    customBlockRenderFunc={customBlockRenderer}
                    handlePastedFiles={handlePastedFiles}
                    toolbar={{
                        options: ['inline', 'blockType', 'list', 'link', 'image', 'history'],
                        inline: {
                            options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                        },
                        blockType: {
                            inDropdown: true,
                            options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                        },
                        list: { options: ['unordered', 'ordered'] },
                        image: {
                            uploadCallback: imageUploadCallback,
                            previewImage: true,
                            alt: { present: true, mandatory: false },
                            inputAccept: 'image/*',
                        },
                    }}
                    toolbarCustomButtons={[<TableOption key="table" onInsertTable={insertTableBlock} />, <CodeSnippetOption key="code" onInsertCodeBlock={insertCodeBlock} />]}
                    editorStyle={{
                        minHeight: 250,
                        padding: '0 15px 40px 15px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        cursor: 'text',
                    }}
                    toolbarStyle={{
                        border: 'none',
                        borderBottom: '1px solid #e0e0e0',
                        marginBottom: 0,
                    }}
                />
            </Paper>

            {/* Media Upload Section */}
            <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Media Files (Images/Videos)
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => fileInputRef.current?.click()}
                        title="Add media files"
                    >
                        <AttachFileIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Thumbnail list of queued files */}
                {mediaPlaceholders.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        {mediaPlaceholders.map((ph, index) => (
                            <Paper
                                key={ph.id}
                                elevation={2}
                                sx={{
                                    position: 'relative',
                                    width: 200,
                                    border: '2px solid #e0e0e0',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => removeMediaFile(index)}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                                        zIndex: 1,
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                                {ph.type === 'image' ? (
                                    <img
                                        src={URL.createObjectURL(ph.file)}
                                        alt={ph.name}
                                        style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 150,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: '#f5f5f5',
                                        }}
                                    >
                                        <video
                                            src={URL.createObjectURL(ph.file)}
                                            style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
                                            controls
                                        />
                                    </Box>
                                )}
                                <Box sx={{ p: 1, bgcolor: '#f9f9f9' }}>
                                    <Typography variant="caption" noWrap title={ph.name}>
                                        {ph.name}
                                    </Typography>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}

                {/* Existing media indicator */}
                {existingMedia && existingMedia.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            💾 {existingMedia.length} existing media file(s) attached
                        </Typography>
                    </Box>
                )}

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    💡 Tip: Paste screenshots from clipboard (Ctrl+V / ⌘+V), drag and drop images, or click the attach icon to add files.
                </Typography>
            </Box>
        </Box>
    );
};

export default RichTextEditor;
